import { useStaticText } from '@websrc/App/Hooks/useStaticText'
import {
  DownloadLanguage as DLanguage,
  mapDownloadAppLanguage,
} from '@websrc/DataMappers/mapDownloadAppLanguage'
import clsx from 'clsx'
import { select, styles } from 'leds'
import React, { ChangeEvent, ComponentPropsWithoutRef, FC } from 'react'
import { match } from 'ts-pattern'

import { Button } from '../Button/Button'

export type DownloadLanguages = ReturnType<typeof mapDownloadAppLanguage>

export type DownloadLanguageProps = ComponentPropsWithoutRef<'div'> & {
  languages: DownloadLanguages
  languageSelected: DownloadLanguages[0]
  updateLanguage: (language: DownloadLanguages[0]) => void
  onDownload?: () => void
}

export const getTextForDownloadType = (
  type: DLanguage['__downloadType'],
  t: ReturnType<typeof useStaticText>
) =>
  match(type)
    .with('file', () => t.downloads.button_text_download)
    .with('store', () => t.downloads.button_text_app)
    .with('webApp', () => t.downloads.launch_web_app)
    .exhaustive()

export const DownloadLanguage: FC<DownloadLanguageProps> = ({
  languages,
  languageSelected,
  updateLanguage,
  onDownload,
  ...divProps
}) => {
  const t = useStaticText()

  const handleSelect = (e: ChangeEvent<HTMLSelectElement>) => {
    const [language] = languages.filter(({ key }) => key === e.target.value)
    updateLanguage(language)
  }

  const displayName = (type: DownloadLanguages[0]) =>
    match(type)
      .with(
        { __downloadType: 'file', lang: 'Global' },
        ({ version }) => `${t.downloads.button_text_download} - ${version}`
      )
      .with({ __downloadType: 'store', lang: 'Global' }, () => t.downloads.button_text_app)
      .with({ __downloadType: 'webApp', lang: 'Global' }, () => t.downloads.launch_web_app)
      .with(
        { __downloadType: 'file' },
        ({ lang, version }) => `${t.downloads.button_text_download} - ${lang} - ${version}`
      )
      .with({ __downloadType: 'store' }, ({ lang }) => `${t.downloads.button_text_app} - ${lang}`)
      .run()

  return (
    <div {...divProps}>
      {match(languageSelected)
        .with({ __downloadType: 'webApp' }, ({ url, __downloadType }) => (
          <Button
            color={'blue'}
            colorMode={'light'}
            dataTestid={`link-${__downloadType}`}
            href={url}
            onClick={onDownload}
            rel={'noreferrer noopener'}
            size={'medium'}
            target={'_blank'}
            text={getTextForDownloadType('webApp', t)}
            type={'primary'}
          />
        ))
        .with({ __downloadType: 'store' }, ({ url, __downloadType }) => (
          <Button
            color={'blue'}
            colorMode={'light'}
            dataTestid={`link-${__downloadType}`}
            href={url}
            onClick={onDownload}
            size={'medium'}
            text={getTextForDownloadType('store', t)}
            type={'primary'}
          />
        ))
        .with({ __downloadType: 'file' }, ({ url, __downloadType, version }) => (
          <Button
            color={'blue'}
            colorMode={'light'}
            dataTestid={`link-${__downloadType}`}
            download={true}
            href={url}
            onClick={onDownload}
            size={'medium'}
            text={`${getTextForDownloadType('file', t)} - ${version}`}
            type={'primary'}
          />
        ))
        .run()}
      <select
        className={clsx(
          select({ type: 'secondary', onlyArrow: true }),
          styles({ marginLeft: 1, height: 'inherit' })
        )}
        onChange={handleSelect}
        value={languageSelected.key}
      >
        {languages.map((type, key) => (
          <option key={key} value={type.key}>
            {displayName(type)}
          </option>
        ))}
      </select>
    </div>
  )
}
